'use client'
import {
  useCurrency
} from "./chunk-XPA6WW2H.mjs";
import {
  ABSOLUTE_FALLBACK_CURRENCY
} from "./chunk-WFHUYKGS.mjs";
import {
  __async,
  __spreadProps,
  __spreadValues
} from "./chunk-QB4ZOYYY.mjs";

// src/components/PriceLabel.tsx
import { twMerge } from "tailwind-merge";
import { Button, Typography as Typography2 } from "@wbk/ui";
import { useTranslation as useTranslation2 } from "@wbk/config";
import { calculateDiscountPercentage } from "@wbk/utils";

// src/components/Symbol.tsx
import { jsx } from "react/jsx-runtime";
var CurrencySymbol = ({ className, currency }) => {
  const { currency: selected } = useCurrency();
  const curr = currency || selected;
  if ((curr == null ? void 0 : curr.svgPath) && (curr == null ? void 0 : curr.svgViewbox)) {
    return /* @__PURE__ */ jsx(
      "svg",
      {
        width: "14",
        height: "14",
        viewBox: curr.svgViewbox,
        fill: "none",
        xmlns: "http://www.w3.org/2000/svg",
        className,
        children: /* @__PURE__ */ jsx("path", { fill: "currentColor", d: curr == null ? void 0 : curr.svgPath })
      }
    );
  }
  return /* @__PURE__ */ jsx("span", { className, children: curr.symbol || curr.shortCode });
};

// src/components/CurrencySwitch.tsx
import { RadioGroup, ResponsiveModal, Typography } from "@wbk/ui";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "@wbk/config";
import { jsx as jsx2, jsxs } from "react/jsx-runtime";
var CurrencySwitch = ({
  render
}) => {
  const { t } = useTranslation("currency_common");
  return /* @__PURE__ */ jsx2(
    ResponsiveModal,
    {
      title: t("currency_common:currency"),
      render,
      body: ({ close }) => /* @__PURE__ */ jsx2(Body, { close }),
      action: {
        label: t("currency_common:apply"),
        form: "localization-selection-form"
      }
    }
  );
};
var Body = ({ close }) => {
  const { setCurrency, currency, currencies } = useCurrency();
  const { control, handleSubmit } = useForm({
    defaultValues: { currency: currency.shortCode }
  });
  const onSubmit = (data) => __async(void 0, null, function* () {
    setCurrency(data.currency);
    close();
  });
  return /* @__PURE__ */ jsx2("form", { id: "localization-selection-form", className: "space-y-4", onSubmit: handleSubmit(onSubmit), children: /* @__PURE__ */ jsx2(
    Controller,
    {
      name: "currency",
      control,
      render: ({ field }) => /* @__PURE__ */ jsx2(
        RadioGroup,
        __spreadProps(__spreadValues({}, field), {
          label: "",
          theme: "action",
          indicator: "check",
          groupClassName: "space-y-4",
          className: "basis-full px-3 md:basis-[calc(50%-16px)] lg:basis-[calc(33%-16px)]",
          options: currencies.map((currency2) => {
            const code = currency2.shortCode;
            const countryCode = code.slice(0, 2);
            return {
              text: /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
                /* @__PURE__ */ jsx2(
                  "img",
                  {
                    className: "h-4 w-6 shrink-0",
                    src: `https://wbk-assets.s3.me-south-1.amazonaws.com/flags/w80/${countryCode.toLowerCase()}.png`,
                    alt: ""
                  }
                ),
                /* @__PURE__ */ jsxs("div", { children: [
                  /* @__PURE__ */ jsx2(Typography, { children: currency2.name }),
                  /* @__PURE__ */ jsx2(Typography, { className: "flex items-center gap-1", color: "text-secondary", children: /* @__PURE__ */ jsx2(CurrencySymbol, { currency: currency2 }) })
                ] })
              ] }),
              value: code
            };
          })
        })
      )
    }
  ) });
};
var CurrencySwitch_default = CurrencySwitch;

// src/components/PriceLabel.tsx
import { jsx as jsx3, jsxs as jsxs2 } from "react/jsx-runtime";
var CurrencyPriceLabel = ({
  discountPrice,
  originalPrice,
  className,
  currency,
  changeable,
  priceClassName,
  currencyClassName,
  zeroAsFree,
  discountPercentageClassName
}) => {
  const { exchangePrice, currency: selected, active } = useCurrency();
  const { t } = useTranslation2("currency_common");
  const selectedEventCurrency = (currency == null ? void 0 : currency.toLowerCase()) || ABSOLUTE_FALLBACK_CURRENCY.shortCode.toLowerCase();
  const hasDiscount = typeof discountPrice === "number" && discountPrice >= 0 && discountPrice < originalPrice;
  const discountPercentage = hasDiscount ? calculateDiscountPercentage(originalPrice, discountPrice) : void 0;
  const price = hasDiscount ? discountPrice : originalPrice;
  return /* @__PURE__ */ jsx3("div", { className: twMerge("group flex items-center gap-2", className), children: price <= 0 && zeroAsFree ? /* @__PURE__ */ jsx3(Typography2, { className: "text-green-500", children: t("currency_common:free") }) : /* @__PURE__ */ jsx3(
    Typography2,
    {
      as: "div",
      className: "group flex shrink-0 items-center gap-px data-[direction=left]:flex-row-reverse",
      "data-direction": selected.direction || "left",
      dir: "ltr",
      children: /* @__PURE__ */ jsxs2("div", { className: "flex items-center gap-1 rtl:flex-row-reverse", children: [
        /* @__PURE__ */ jsxs2("div", { className: "flex items-center gap-1", children: [
          active && changeable ? /* @__PURE__ */ jsx3(
            CurrencySwitch_default,
            {
              render: ({ open }) => /* @__PURE__ */ jsx3(
                Button,
                {
                  onClick: open,
                  className: twMerge("rounded-sm p-0 underline", currencyClassName),
                  theme: "transparent",
                  shape: "text",
                  "data-testid": "currency_tickets_currency_switch",
                  children: /* @__PURE__ */ jsx3(CurrencySymbol, {})
                }
              )
            }
          ) : /* @__PURE__ */ jsx3(CurrencySymbol, { className: currencyClassName }),
          /* @__PURE__ */ jsx3(Typography2, { as: "span", className: priceClassName, children: exchangePrice(price, selectedEventCurrency) })
        ] }),
        hasDiscount ? /* @__PURE__ */ jsxs2("div", { className: "flex items-center gap-1 rtl:flex-row-reverse", children: [
          /* @__PURE__ */ jsxs2(
            Typography2,
            {
              as: "span",
              variant: "body-XS",
              className: twMerge(
                "bg-success/15 text-success rounded-XS px-1 py-px",
                discountPercentageClassName
              ),
              children: [
                discountPercentage,
                "%"
              ]
            }
          ),
          /* @__PURE__ */ jsxs2(
            Typography2,
            {
              as: "span",
              variant: "body-S",
              className: "flex items-center gap-0.5 line-through",
              color: "text-secondary",
              children: [
                /* @__PURE__ */ jsx3(CurrencySymbol, {}),
                /* @__PURE__ */ jsx3("span", { className: priceClassName, children: exchangePrice(originalPrice, selectedEventCurrency) })
              ]
            }
          )
        ] }) : null
      ] })
    }
  ) });
};

export {
  CurrencySymbol,
  CurrencyPriceLabel
};
//# sourceMappingURL=chunk-V54S65XE.mjs.map